import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Box, Button, Dialog, DialogActions, DialogContent, Grid2 } from '@mui/material'
import Input from '../../../components/forms/Input'
import i18n from 'simple-react-i18n'
import AdministrationAction from '../../../administration/actions/AdministrationAction'
import Select from '../../../components/forms/Select'
import useTitle from '../../../utils/customHook/useTitle'
import useActions from '../../../utils/customHook/useActions'
import { DefaultDialogTitle } from '../../../components/styled/Dialog'
import { StyledFieldSet } from '../../../components/StyledElements'
import ReferencialAction from '../../../referencial/action/ReferencialAction'
import MultiContributorsAutocomplete from '../../../referencial/components/contributor/components/MultiContributorsAutocomplete'
import useSandreList from '../../../utils/customHook/useSandreList'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import { WhiteCard } from 'components/styled/Card'
import { ButtonMUI } from 'components/styled/Buttons'
import PerimetersFolderTable from './PerimetersFolderTable'
import PerimetersAction from '../../action/PerimetersAction'

const PERIMETERS_FOLDER_HEADER = ['name', 'city', 'collectivity', 'nbPerimeters', 'stateOfProgress', 'status']

const FilterPanel = () => {
    const {
        perimetersFolder,
        selectedSearchValues,
        cities,
        contributors,
    } = useSelector(store => ({
        perimetersFolder: store.PerimetersReducer.perimetersFolder,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        cities: store.CityReducer.cities,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const [searchValue, setSearchValue] = useState(selectedSearchValues.perimetersFolder?.searchValue ?? '')
    const [status, setStatus] = useState(selectedSearchValues.perimetersFolder?.status)
    const [state, setState] = useState(selectedSearchValues.perimetersFolder?.state)
    const [city, setCity] = useState(selectedSearchValues.perimetersFolder?.city)
    const [collectivity, setCollectivity] = useState(selectedSearchValues.perimetersFolder?.collectivity)

    const perimetersStatus = useSandreList('PARCELLES.STATUT')
    const perimetersStates = useSandreList('PARCELLES.ETAT')

    const dispatch = useDispatch()

    const onLaunchSearch = () => {
        dispatch(AdministrationAction.setCache('perimetersFolder', { searchValue, status, state, city, collectivity }))
        dispatch(AdministrationAction.setSelectedSearchValues('perimetersFolder', { searchValue, status, state, city, collectivity }))
    }

    const onReset = () => {
        setSearchValue('')
        setStatus(undefined)
        setState(undefined)
        setCity(undefined)
        setCollectivity(undefined)
        onLaunchSearch()
    }

    return (
        <WhiteCard round noMargin={false} cardStyle={{ width: '100%' }}>
            <Grid2 container alignItems='baseline' sx={{ padding: '1rem' }} columnSpacing={2}>
                <Grid2 size={4}>
                    <Input
                        title={i18n.search}
                        tooltip={i18n.searchThenEnter}
                        value={searchValue}
                        onChange={setSearchValue}
                        onEnterKeyPress={v => {
                            setSearchValue(v)
                            onLaunchSearch()
                        }}
                        clearFunction
                        data-cy='search'
                    />
                </Grid2>
                <Grid2 size={4}>
                    <Select
                        options={perimetersStates}
                        label={i18n.stateOfProgress}
                        nullLabel='&nbsp;'
                        value={state}
                        noSort
                        onChange={setState}
                        data-cy='stateOfProgress'
                    />
                </Grid2>
                <Grid2 size={4}>
                    <Select
                        options={perimetersStatus}
                        label={i18n.status}
                        nullLabel='&nbsp;'
                        value={status}
                        noSort
                        onChange={setStatus}
                        data-cy='status'
                    />
                </Grid2>
                <Grid2 container size={9} columnSpacing={2} alignItems='center'>
                    <Grid2 size={6}>
                        <Select
                            options={cities.filter(c => perimetersFolder.some(perimeterFolder => perimeterFolder.cities.includes(c.id)))}
                            label={i18n.city}
                            nullLabel='&nbsp;'
                            value={city}
                            noSort
                            onChange={setCity}
                            data-cy='city'
                        />
                    </Grid2>
                    <Grid2 size={6}>
                        <MultiContributorsAutocomplete
                            label={i18n.collectivity}
                            onChange={setCollectivity}
                            values={collectivity}
                            options={contributors.filter(c => perimetersFolder.some(perimeterFolder => perimeterFolder.collectivity === c.id))}
                            data-cy='collectivity'
                        />
                    </Grid2>
                </Grid2>
                <Grid2 container size={3} justifyContent='flex-end' alignItems='center' columnSpacing={1}>
                    <ButtonMUI variant='outlined' onClick={onReset}>
                        {i18n.reinit}
                    </ButtonMUI>
                    <ButtonMUI variant='contained' onClick={() => onLaunchSearch()} data-cy='submit'>
                        {i18n.search}
                    </ButtonMUI>
                </Grid2>
            </Grid2>
        </WhiteCard>
    )
}

const PerimetersDashboard = () => {
    const {
        perimetersFolder,
        contributors,
        sandreCodes,
    } = useSelector(store => ({
        perimetersFolder: store.PerimetersReducer.perimetersFolder,
        contributors: store.ContributorReducer.contributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const [createName, setCreateName] = useState()
    const [createStatus, setCreateStatus] = useState()
    const [createState, setCreateState] = useState()
    const [createCollectivity, setCreateCollectivity] = useState()

    const [addPopupIsOpen, setAddPopupIsOpen] = useState(false)

    const dispatch = useDispatch()

    const perimetersStatus = useSandreList('PARCELLES.STATUT')
    const perimetersStates = useSandreList('PARCELLES.ETAT')

    const canBeCreated = createName || createStatus || createState || createCollectivity

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
    }, [])

    useTitle(() => [{
        title: i18n.perimetersFolder,
        href: 'perimeter',
    }], [])

    useActions(() => ({
        export: () => {
            return {
                data: perimetersFolder.map(d => ({
                    identifier: { value: d.folderId?.toString() ?? '' },
                    name: { value: d.name },
                    city: { value: d.cityName },
                    collectivity: { value: contributors.find(c => c.id === d.collectivity)?.name ?? '' },
                    nbPerimeters: { value: d.perimeters?.toString() ?? '0' },
                    status: { value: perimetersStatus.find(s => s.code === d.status)?.name ?? '' },
                    stateOfProgress: { value: perimetersStates.find(s => s.code === d.state)?.name ?? '' },
                    headers: ['identifier', ...PERIMETERS_FOLDER_HEADER],
                })),
                exportType: 'xlsx',
                titleFile: i18n.perimetersFolder,
            }
        },
        new: () => {
            setCreateName(undefined)
            setCreateStatus(undefined)
            setCreateState(0)
            setCreateCollectivity(undefined)

            setAddPopupIsOpen(true)
        },
    }), [perimetersFolder, sandreCodes, contributors])

    const onCloseDialog = () => {
        setCreateName(undefined)
        setCreateStatus(undefined)
        setCreateState(undefined)
        setCreateCollectivity(undefined)

        setAddPopupIsOpen(false)
    }

    return (
        <Box sx={{ margin: '1rem 1rem 0.5rem 0.5rem' }}>
            <FilterPanel />
            <Grid2 container size={12} sx={{ paddingTop: '1rem' }}>
                <PerimetersFolderTable/>
            </Grid2>
            <Dialog
                fullWidth
                maxWidth='lg'
                open={addPopupIsOpen}
                onClose={onCloseDialog}
                slotProps={{
                    paper: {
                        sx: {
                            minHeight: { xl: '35vh', lg: '52vh', xs: 'auto' },
                            maxHeight: { xl: '35vh', lg: '52vh', xs: 'auto' },
                        },
                    },
                }}
            >
                <DefaultDialogTitle
                    title={i18n.createPerimetersFolder}
                    onClose={onCloseDialog}
                />
                <DialogContent>
                    <StyledFieldSet style={{ padding: '30px 50px' }}>
                        <Grid2 container spacing={2}>
                            <Grid2 size={12}>
                                <Input
                                    title={i18n.name}
                                    value={createName}
                                    onChange={setCreateName}
                                    clearFunction
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <Select
                                    options={perimetersStates}
                                    label={i18n.stateOfProgress}
                                    nullLabel='&nbsp;'
                                    value={createState}
                                    noSort
                                    onChange={setCreateState}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <MultiContributorsAutocomplete
                                    label={i18n.collectivity}
                                    onChange={setCreateCollectivity}
                                    values={createCollectivity}
                                    options={contributors}
                                    disablePortal={false}
                                />
                            </Grid2>
                        </Grid2>
                    </StyledFieldSet>
                </DialogContent>
                <DialogActions>
                    <Button
                        color='primary'
                        variant='contained'
                        onClick={() => {
                            if (canBeCreated) {
                                dispatch(PerimetersAction.createPerimetersFolder({
                                    name: createName,
                                    status: createStatus,
                                    state: createState,
                                    collectivity: createCollectivity,
                                })).then(onCloseDialog)
                            } else {
                                dispatch(ToastrAction.error(i18n.pleaseEnterAtLeastOneParameter))
                            }
                        }}
                    >
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default PerimetersDashboard